<script lang="ts" module>
	import lastImageXs from "@assets/images/gift-subscription-cafe.jpg?webp&h=1000&imagetools";
	import lastImageSm from "@assets/images/gift-subscription-cafe.jpg?webp&h=1300&imagetools";
	import lastImageLg from "@assets/images/gift-subscription-cafe.jpg?webp&h=1600&imagetools";
	import lastImage3xl from "@assets/images/gift-subscription-cafe.jpg?webp&imagetools";
	import giftSubscriptionLogoCs from "@assets/images/gift-subscription-logo-cs.png?webp&w=900&imagetools";
	import giftSubscriptionLogoEn from "@assets/images/gift-subscription-logo-en.png?webp&w=900&imagetools";
	import contentImageXs from "@assets/images/gift-subscription-photo.jpg?webp&h=480&imagetools";
	import contentImageSm from "@assets/images/gift-subscription-photo.jpg?webp&h=640&imagetools";
	import contentImageLg from "@assets/images/gift-subscription-photo.jpg?webp&h=800&imagetools";
	import contentImage2xl from "@assets/images/gift-subscription-photo.jpg?webp&imagetools";
	import FloatingCta from "@components/FloatingCta.svelte";
	import GiftSubscriptionButton from "@components/GiftSubscriptionButton.svelte";
	import GiftSubscriptionHero from "@components/GiftSubscriptionHero.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import { ButtonVariant } from "@components/common/ButtonVariant.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import AboutUsContentSection from "@components/sections/AboutUsContentSection.svelte";
	import BenefitsStrip from "@components/sections/BenefitsStrip.svelte";
	import GiftSubscriptionAdvantagesSection from "@components/sections/GiftSubscriptionAdvantagesSection.svelte";
	import GiftSubscriptionFaqSection from "@components/sections/GiftSubscriptionFaqSection.svelte";
	import GiftSubscriptionHowItWorksSection from "@components/sections/GiftSubscriptionHowItWorksSection.svelte";
	import ImageSection from "@components/sections/ImageSection.svelte";
	import SubscriptionKindsSection from "@components/sections/SubscriptionKindsSection.svelte";
	import TaglineStrip from "@components/sections/TaglineStrip.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { Locale, type LocalizedString } from "@core/schema/Locale.js";
	import { getHowItWorksAnchor } from "@core/schema/paths/getHowItWorksAnchor.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import translations from "./GiftSubscription.translations.json" with { type: "json" };
	import type { GiftSubscriptionProps } from "./GiftSubscriptionProps.js";

	const logoLocaleMap: LocalizedString = {
		[Locale.cs]: giftSubscriptionLogoCs,
		[Locale.en]: giftSubscriptionLogoEn,
	};
	const heroImageHeight = tw("!h-[max(calc(100vh-8rem),32rem)]");
</script>

<script lang="ts">
	const translate = getTranslate(translations);
	const { tenant }: GiftSubscriptionProps = $props();
</script>

<Layout transparentHeader>
	<GiftSubscriptionHero>
		<GiftSubscriptionButton variant={ButtonVariant.OutlinedWhite} />
		<Link href={getHowItWorksAnchor(tenant.locale)} size={ButtonSize.Medium} variant={ButtonVariant.White}>
			<Formatted text={translate("howItWorks")} />
		</Link>
	</GiftSubscriptionHero>
	<BenefitsStrip items={[translate("benefitGift"), translate("benefitFreeDelivery"), translate("benefitPrice")]} />
	<section class="py-14 sm:py-17 md:py-20 text-center container-6">
		<p class="font-antipol font-medium text-8/tighter text-fcrdarkgray mb-8">
			<Formatted text={translate("introTextLine1")} />
		</p>
		<p class="text-sm/tight sm:text-lg/normal">
			<Formatted text={translate("introTextLine2")} />
		</p>
	</section>
	<GiftSubscriptionHowItWorksSection />
	<AboutUsContentSection imageLeft={false} mediaClass="xl:w-150 xl:h-192">
		{#snippet heading()}
			<div class="flex flex-col">
				<div class="uppercase text-fcrorange font-bold tracking-[.1em]">
					<Formatted text={translate("contentSection")} />
				</div>
				<Heading
					level={2}
					text={translate("contentTitle")}
					class="font-antipol font-medium text-8/tighter text-fcrdarkgray mt-4"
				/>
			</div>
		{/snippet}
		<p class="text-5/tighter mt-4">
			<Formatted text={translate("contentParagraph")} />
		</p>
		{#snippet media()}
			<div class="absolute inset-0 flex flex-col justify-end items-center pb-8 sm:pb-12">
				<Media
					width={372}
					height={560}
					source={{ type: "image/webp", src: contentImageXs }}
					sourcesets={{
						">sm": contentImageSm,
						">lg": contentImageLg,
						">2xl": contentImage2xl,
					}}
					alt=""
					class="size-full object-cover"
					wrapperClass="absolute inset-0"
				/>
				<GiftSubscriptionButton variant={ButtonVariant.White} />
			</div>
		{/snippet}
	</AboutUsContentSection>
	<GiftSubscriptionAdvantagesSection />
	<GiftSubscriptionFaqSection />
	<hr class="border-fcrdarkgray border-solid full-width" />
	<SubscriptionKindsSection />
	<TaglineStrip />
	<ImageSection
		sources={{
			default: lastImageXs,
			">sm": lastImageSm,
			">lg": lastImageLg,
			">3xl": lastImage3xl,
		}}
		darkOverlay
		class={[heroImageHeight]}
	>
		{#snippet heading()}
			<Heading
				level={2}
				text={translate("heroTitle")}
				class="absolute min-w-45 w-3/4 max-w-183 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
			>
				<Media
					width={410}
					height={230}
					sourcesets={{}}
					class="w-full"
					source={{ type: "image/webp", src: logoLocaleMap[tenant.locale] }}
					alt={translate("heroTitle")}
				/>
			</Heading>
		{/snippet}
		{#snippet actions()}
			<GiftSubscriptionButton variant={ButtonVariant.White} />
		{/snippet}
	</ImageSection>
	<FloatingCta>
		<GiftSubscriptionButton class="w-full sm:w-fit" variant={ButtonVariant.Dark} />
	</FloatingCta>
</Layout>
